/**
 * Shared derivations for slot schedule hooks: indexes loaded slots, validates the selected interval,
 * and builds the time-picker disable callback. Query wiring stays in the resource/service wrappers.
 */

import { useCallback, useMemo } from 'react';
import type { Dayjs } from 'dayjs';
import type { AvailableSlot } from '@/api/mcp-appointments';
import {
  buildDisabledTimeFromAllowedTimes,
  buildSlotsByDateKey,
  buildSlotsLocalTimesByDateKey,
  DISABLE_ALL_TIMES_CONFIG,
  getCalendarDateKey,
  getCalendarMonthKey,
  getMonthKeyFromDayjs,
  isSameSlot,
  normalizeIso
} from './utils';

/** Query outcome and selection inputs shared by resource and service slot schedules. */
export interface UseSlotScheduleCoreProps {
  /** Slots returned for the visible month, or `undefined` before the query resolves. */
  slots: AvailableSlot[] | undefined;
  /** Whether the slot query is loading for the first time. */
  isLoading: boolean;
  /** Whether the slot query is (re)fetching. */
  isFetching: boolean;
  /** Whether a slot result is available to judge the selection against (gates `slotUnavailable`). */
  hasSlotsResult: boolean;
  /** IANA timezone the slots are indexed in. */
  timezone: string;
  /** Minute step the picker enforces when offering times. */
  slotAlignmentMinutes: number;
  /** Month currently shown in the slot calendar. */
  visibleMonth: Dayjs;
  /** Selected appointment start. */
  startTime: Dayjs | null | undefined;
  /** Derived appointment end from start time and duration. */
  endTime: Dayjs | null | undefined;
  /** Whether the user has committed a concrete start time (not just a day). */
  hasCommittedStartTime: boolean;
  /** Existing slot that should be treated as valid even if absent from fresh results. */
  treatAsValidSlot: { start: Dayjs; end: Dayjs } | null;
  /** Date-picker disable rule; differs between resource policy and service bookings. */
  disabledDate: (current: Dayjs | null) => boolean;
}

/**
 * Derives picker constraints and selection validity from already-loaded slot data.
 * @returns Disabled date/time handlers, loading flags, validation outcome, and indexed slot data.
 */
export const useSlotScheduleCore = ({
  slots,
  isLoading,
  isFetching,
  hasSlotsResult,
  timezone,
  slotAlignmentMinutes,
  visibleMonth,
  startTime,
  endTime,
  hasCommittedStartTime,
  treatAsValidSlot,
  disabledDate
}: UseSlotScheduleCoreProps) => {
  const loadedMonthKey = useMemo(
    () => getMonthKeyFromDayjs(visibleMonth.startOf('month')) ?? visibleMonth.format('YYYY-MM'),
    [visibleMonth]
  );
  const selectedMonthKey = startTime?.isValid()
    ? getCalendarMonthKey(startTime) ?? getMonthKeyFromDayjs(startTime)
    : null;
  const isSelectedMonthLoaded = !!selectedMonthKey && selectedMonthKey === loadedMonthKey;
  const hasSlots = !!slots?.length;

  const slotStartEnds = useMemo(() => {
    const startEndPairs = new Set<string>();
    if (!slots) {
      return { startEndPairs };
    }

    for (const slot of slots) {
      startEndPairs.add(`${normalizeIso(slot.start)}|${normalizeIso(slot.end)}`);
    }

    return { startEndPairs };
  }, [slots]);

  const slotsLocalTimesByDateKey = useMemo(
    () => buildSlotsLocalTimesByDateKey(slots, timezone),
    [slots, timezone]
  );

  const slotsByDateKey = useMemo(
    () => buildSlotsByDateKey(slots, timezone),
    [slots, timezone]
  );

  const disabledTimeByDateKey = useMemo(() => {
    const cache = new Map<string, ReturnType<typeof buildDisabledTimeFromAllowedTimes>>();
    for (const [dateKey, allowedLocalTimes] of slotsLocalTimesByDateKey) {
      cache.set(dateKey, buildDisabledTimeFromAllowedTimes(allowedLocalTimes, slotAlignmentMinutes));
    }
    return cache;
  }, [slotsLocalTimesByDateKey, slotAlignmentMinutes]);

  const isSelectedSlotValid = useMemo(() => {
    if (!startTime || !endTime || !startTime.isValid() || !endTime.isValid()) {
      return false;
    }

    if (
      treatAsValidSlot
      && isSameSlot(treatAsValidSlot.start, treatAsValidSlot.end, startTime, endTime)
    ) {
      return true;
    }

    if (!isSelectedMonthLoaded) {
      return true;
    }

    if (isLoading || isFetching) {
      return false;
    }

    const start = normalizeIso(startTime.toISOString());
    const end = normalizeIso(endTime.toISOString());
    return slotStartEnds.startEndPairs.has(`${start}|${end}`);
  }, [
    startTime,
    endTime,
    slotStartEnds,
    treatAsValidSlot,
    isSelectedMonthLoaded,
    isLoading,
    isFetching
  ]);

  const slotUnavailable = useMemo(() => {
    if (!hasCommittedStartTime || !startTime || !endTime || !hasSlotsResult || !isSelectedMonthLoaded) {
      return null;
    }
    if (isSelectedSlotValid) {
      return null;
    }
    if (isLoading || isFetching) {
      return null;
    }
    return { hasViolation: true as const };
  }, [
    hasCommittedStartTime,
    startTime,
    endTime,
    hasSlotsResult,
    isSelectedMonthLoaded,
    isSelectedSlotValid,
    isLoading,
    isFetching
  ]);

  const disabledTime = useCallback(
    (current: Dayjs | null) => {
      if (!current?.isValid() || isFetching || !hasSlots) {
        return DISABLE_ALL_TIMES_CONFIG;
      }

      const currentMonthKey = getCalendarMonthKey(current);
      if (!currentMonthKey || currentMonthKey !== loadedMonthKey) {
        return DISABLE_ALL_TIMES_CONFIG;
      }

      const dateKey = getCalendarDateKey(current);
      if (!dateKey) {
        return DISABLE_ALL_TIMES_CONFIG;
      }
      return disabledTimeByDateKey.get(dateKey) ?? DISABLE_ALL_TIMES_CONFIG;
    },
    [hasSlots, disabledTimeByDateKey, isFetching, loadedMonthKey]
  );

  const isCheckingSlots = isLoading || isFetching;
  const isCheckingSelectedMonthSlots = isCheckingSlots && isSelectedMonthLoaded;
  const isLoadingVisibleMonthSlots = isCheckingSlots;

  return {
    disabledDate,
    disabledTime,
    isCheckingSlots,
    isLoadingVisibleMonthSlots,
    isCheckingSelectedMonthSlots,
    isSelectedSlotValid,
    slotUnavailable,
    slotAlignmentMinutes,
    timezone,
    slots,
    slotsByDateKey
  };
};

/** Shape returned by `useSlotScheduleCore`, consumed by the slot selection hooks. */
export type SlotScheduleCoreResult = ReturnType<typeof useSlotScheduleCore>;
